import React, { useState, useEffect, useContext } from "react";
import "./BillingTable.scss";
import BillingDataTable from "./BillingDataTable";
import { downloadInvoice, downloadInvoiceMuliple, invoiceList } from "../../services/Subscription";
import CommonUtil from "../../util/CommonUtils";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import AppContext from "../../config/AppContext";
import { ReactComponent as DownloadIcon } from "../../assets/subscription/downloadInvoice-icon.svg";
import { ReactComponent as DownloadIconDark } from "../../assets/subscription/downloadInvoiceDark-icon.svg";
import { toast } from "sonner";


const BillingTable = () => {
  const { mode } = useContext(AppContext);
  const [invoice, setInvoice] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const role = CommonUtil.getRoleCode();
  const [tableKey, setTableKey] = useState(0);

  // const formattedDate = (dateString) => {
  //   const date = new Date(dateString);
  //   const options = {
  //     day: "2-digit",
  //     month: "short",
  //     year: "numeric",
  //   };
  //   return date.toLocaleDateString(i18n.language, options);
  // };
  const formattedDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    loadInvoiceList(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const loadInvoiceList = (pageNumber, pageSize) => {
    if (isLoading) return;
    setIsLoading(true);
    invoiceList({ pageNumber, pageSize })
      .then((data) => {
        if (data?.data?.code === "RCI0000") {
          setInvoice(data?.data?.data?.invoices);
          setTotalRecords(data?.data?.data?.totalCount);
          setPage(data?.data?.data?.currentPage);
        } else {
          setInvoice([]);
          setTotalRecords(0);
          setPage(1);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("API call error:", error);
        setIsLoading(false);
      });
  };

  const columns = React.useMemo(() => [
    {
      field: "Number",
      headerName: t("RAL0909"),
      sortable: false,
      flex: 1,
      width: 10,
      renderCell: (params) => (
        <Tooltip
          title={params.row.invoiceNumber}
          placement="bottom-start"
          disableInteractive
        >
          <span style={{ textTransform: "none" }}>{params.row.invoiceNumber}</span>
        </Tooltip>
      ),
    },
    {
      field: "due_date",
      headerName: t("RAL0970"),
      sortable: false,
      flex: 1,
      width: 10,
      renderCell: (params) => (
        <Tooltip
          title={params.row.invoiceDate ? formattedDate(params.row.invoiceDate) : ""}
          placement="bottom-start"
          disableInteractive
        >
          {params.row.invoiceDate ? formattedDate(params.row.invoiceDate) : ""}
        </Tooltip>
      ),
    },

    {
      field: "customer_id",
      headerName: t("RAL0911"),
      sortable: false,
      flex: 1,
      width: 10,
      renderCell: (params) => {
        const plan = params.row.plan || params.row.subscription.plan;
        return (
          <Tooltip
            title={plan.name}
            placement="bottom-start"
            disableInteractive
          >
            <span style={{ textTransform: "none" }}>{plan.name}</span>
          </Tooltip>
        );
      },
    },

    {
      field: "total",
      headerName: t("RAL0912"),
      sortable: false,
      flex: 1,
      width: 10,
      renderCell: (params) => (
        <Tooltip
          title={params.row.paymentMade}
          placement="bottom-start"
          disableInteractive
        >
          <span style={{ textTransform: "none" }}>$ {params.row.paymentMade}</span>
        </Tooltip>
      ),
    },

    role !== "EMPLOYEE" &&
    role !== "CLIENT" &&
    role !== "TEAM LEAD" && {
      // headerName: t("RAL0538"),
      type: "actions",
      flex: 1,
      width: 10,
      headerAlign: "left",
      renderCell: (params) => {
        return (
          <>
            <button className="billingtablebutton" onClick={() =>
              handleDownload(params.row.id, params.row.invoiceNumber
              )}>
              <span className={mode === "dark" ? "download-invoicelinkDark" : "download-invoicelink"}>{t("RAL0913")}
                {mode === "dark" ? <DownloadIconDark /> : <DownloadIcon />}
              </span>
            </button>
          </>
        );
      },
    },
  ]);

  const handleSelectionChange = (selected) => {
    setSelectedRows(selected);
  };
  const handleDownload = async (id, invoiceNumber) => {
    try {
      await downloadInvoice(id, invoiceNumber, t("RAL0880"));
      toast.success(t("RAL0942"));
    } catch (error) {
      toast.error("Download failed. Please try again.");
    }
    handleCancel();
  };

  const handleDownloadSelected = () => {
    if (selectedRows.length === 0) {
      return
    } else if (selectedRows.length === 1) {
      selectedRows.forEach(id => {
        const selectedInvoice = invoice.find(inv => inv.id === id);
        if (selectedInvoice) {
          downloadInvoice(
            selectedInvoice.id,
            selectedInvoice.invoiceNumber,
            t('RAL0880')
          );
          toast.success(t("RAL0942"));
        }
      });
    // } else {
    //   downloadInvoiceMuliple(selectedRows, t('RAL0880'));
    //   toast.success(t("RAL0942"));
    // }

    }
      else {
      // Prepare a list of InvoiceDownloadDTO objects
      const invoiceList = selectedRows.map(id => {
        const selectedInvoice = invoice.find(inv => inv.id === id);
        return selectedInvoice
          ? { invoiceId: selectedInvoice.id, invoiceNumber: selectedInvoice.invoiceNumber }
          : null;
      }).filter(item => item !== null); // Remove any null values
  
      if (invoiceList.length > 0) {
        downloadInvoiceMuliple(invoiceList, t('RAL0880'));
        toast.success(t("RAL0942"));
      }
    }
    handleCancel();
  };

  const handleCancel = () => {
    setSelectedRows([]);
    setTableKey(prev => prev + 1);
  };

  return (
    <div className="BillingTableContainer">
      <div className={mode === "dark" ? "BillingHeaderDark" : "BillingHeader"}>
        <div className="BillingHistoryContainer">
          <span className={mode === "dark" ? "BillingHistoryLabelDark" : "BillingHistoryLabel"}>{t("RAL0914")} </span>
          <span className={mode === "dark" ? "BillingHistoryContentDark" : "BillingHistoryContent"}>
            {t("RAL0915")}
          </span>
        </div>
        {selectedRows.length > 0 && (
          <div className="BillingActions">
            <span className={mode === "dark" ? "SelectedCountBillingDark" : "SelectedCountBilling"}>
              {selectedRows.length === totalRecords ? t('RAL0933') : `${selectedRows.length} ${t('RAL0934')}`}
            </span>

            <button className="DownloadBillingButton" onClick={handleDownloadSelected}>{t("RAL0916")}</button>
            <button className={mode === "dark" ? "CancelBillingButtonDark" : "CancelBillingButton"} onClick={handleCancel}>
              {t("RAL0177")}
            </button>
          </div>
        )}
      </div>

      <BillingDataTable
        page={page - 1}
        totalRecords={totalRecords}
        rowsPerPage={rowsPerPage}
        columns={columns}
        rows={invoice}
        checkboxSelection={true}
        disableRowSelectionOnClick={true} // Prevents row selection via row clicks
        key={tableKey}
        selectionModel={selectedRows}
        rowSelection={true}
        onRowSelectionModelChange={setSelectedRows}
        onSelectionChange={handleSelectionChange}
        onPageChange={(e) => {
          const CurrentPage = CommonUtil.getPageNumber(e);
          setPage(CurrentPage);
        }}
        onPageSizeChange={(e) => {
          setRowsPerPage(e);
          setPage(1);
        }}
        onCellClick={(params, event) => {
          if (params.field !== "__check__") {
            event.stopPropagation(); // Prevents unintended row selection
          }
        }}
      />
    </div>
  );
};

export default BillingTable;
